import { IMAGES } from "@/lib/images";
import ParallaxImage from "@/components/ParallaxImage";
import { LuxeLink } from "@/components/LuxeLink";
import { Container, Overline, SectionHeading, Reveal } from "@/components/primitives";

const STEPS = [
  { n: "01", t: "Invitation or Request", d: "Access the selection process through a private invitation, or request consideration for your property." },
  { n: "02", t: "Individual Review", d: "The VillaFlow Selection Team reviews your property online and against the Selection Standard." },
  { n: "03", t: "Assessment", d: "Properties are assessed across six areas, alongside non-negotiable standards of trust." },
  { n: "04", t: "Selection", d: "Approved properties become VillaFlow Selected — part of a curated hospitality network." },
];

const PILLARS = [
  { t: "Selected", d: "Every property is individually reviewed before joining." },
  { t: "Limited", d: "The Collection remains intentionally selective." },
  { t: "Connected", d: "Every selected property becomes part of a curated hospitality network." },
];

export default function PrivateCollection() {
  return (
    <div className="relative z-10">
      <section className="pt-40 md:pt-52 pb-8" data-testid="private-collection-page">
        <Container>
          <div className="max-w-4xl">
            <Reveal>
              <Overline>The Private Collection</Overline>
              <SectionHeading className="mt-8">Private by design.</SectionHeading>
              <p className="mt-8 text-ivory/65 text-lg md:text-xl font-light leading-relaxed">
                VillaFlow Private Collection is intentionally limited. Every property is individually
                reviewed before joining. Membership is not automatically granted — it is designed to
                preserve quality, consistency, exclusivity, guest trust and brand reputation.
              </p>
            </Reveal>
          </div>
        </Container>
      </section>

      <Container className="py-16">
        <div className="grid md:grid-cols-3 gap-px bg-ivory/10 border border-ivory/10">
          {PILLARS.map((p, i) => (
            <Reveal key={p.t} delay={i * 0.1}>
              <div className="bg-navy-900 p-10 md:p-12 h-full">
                <span className="font-serif text-gold text-lg tracking-[0.25em] uppercase">{p.t}</span>
                <p className="mt-5 text-ivory/55 font-light leading-relaxed">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>

      <ParallaxImage src={IMAGES.yacht} alt="Amalfi Coast" height="h-[60vh]" overlay="bg-navy-900/45" />

      {/* How it works */}
      <section className="py-28 md:py-40 bg-navy-800 border-y border-ivory/10">
        <Container>
          <div className="max-w-3xl mb-20">
            <Reveal>
              <Overline>How It Works</Overline>
              <SectionHeading className="mt-8">A considered path to selection.</SectionHeading>
            </Reveal>
          </div>
          <div className="space-y-px bg-ivory/10 border border-ivory/10">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06}>
                <div className="bg-navy-800 grid md:grid-cols-12 gap-6 p-10 md:p-12 items-baseline group hover:bg-navy-700 transition-colors duration-700">
                  <span className="md:col-span-2 font-serif text-gold/40 text-5xl group-hover:text-gold/70 transition-colors duration-700">{s.n}</span>
                  <h3 className="md:col-span-4 font-serif text-2xl md:text-3xl text-ivory">{s.t}</h3>
                  <p className="md:col-span-6 text-ivory/55 font-light leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-28 md:py-40 text-center">
        <Container>
          <Reveal>
            <p className="font-serif font-light text-3xl md:text-6xl text-ivory max-w-4xl mx-auto leading-[1.1] tracking-tight">
              VillaFlow is not a software you subscribe to.
              <br /><span className="text-gold italic">It is a standard you belong to.</span>
            </p>
            <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4">
              <LuxeLink to="/invitation" variant="solid">I Have an Invitation</LuxeLink>
              <LuxeLink to="/apply" variant="outline">Request Consideration</LuxeLink>
            </div>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
